import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Loader2, Lock, ShieldAlert, Video } from 'lucide-react';
import {
  gameshowApi,
  pointsNow,
  GameShowApiError,
  type ContestantStateResponse,
  type StateCurrentQuestion,
} from '@/services/gameshowApi';

// ── /show-stage/:showId ───────────────────────────────────────────────
// Desktop-only CONTESTANT page. You've been drawn (Come On Down) and you're
// on camera — questions are shown ON SCREEN here (visibility is the anti-cheat,
// per the product doc), with copy-blocked delivery as belt-and-suspenders.
// Polls GET /contestant-state every 2s; answers reuse POST /answer.

const PAGE_BG = 'radial-gradient(120% 60% at 30% -10%, #241a34 0%, #14101d 55%, #0c0812 100%)';
const GOLD = '#ffd600';
const PINK = '#f286c9';
const GREEN = '#8cc63f';
const REC = '#ff4d6a';
const LETTERS = ['A', 'B', 'C', 'D', 'E', 'F'];

// Normalize openedAt (ms number, numeric string, or ISO) → ms epoch.
function toMs(v: number | string): number {
  if (typeof v === 'number') return v;
  const n = Number(v);
  if (!Number.isNaN(n) && String(v).trim() !== '') return n;
  return new Date(v).getTime();
}

const kicker: React.CSSProperties = {
  fontSize: 10,
  letterSpacing: '0.14em',
  textTransform: 'uppercase',
  fontWeight: 600,
};

const blockCopy = (e: React.SyntheticEvent) => e.preventDefault();

const GameShowContestantPage: React.FC = () => {
  const { showId } = useParams<{ showId: string }>();

  const [st, setSt] = useState<ContestantStateResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notContestant, setNotContestant] = useState(false);
  const [, setTick] = useState(0);

  const offsetRef = useRef(0);
  const earnedRef = useRef<Record<string, number>>({});
  const [pending, setPending] = useState<{ qid: string; idx: number } | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const poll = useCallback(async () => {
    if (!showId) return;
    try {
      const next = await gameshowApi.contestantState(showId);
      offsetRef.current = next.currentQuestion
        ? toMs(next.currentQuestion.serverNow) - Date.now()
        : 0;
      setSt(next);
      setError(null);
      setNotContestant(false);
    } catch (e) {
      if (e instanceof GameShowApiError && (e.code === 'NOT_A_CONTESTANT' || e.status === 403)) {
        setNotContestant(true);
        setError(null);
        return;
      }
      setError(e instanceof Error ? e.message : 'Lost connection to the stage.');
    }
  }, [showId]);

  useEffect(() => {
    poll();
    const id = setInterval(poll, 2000);
    return () => clearInterval(id);
  }, [poll]);

  // Local 100ms ticker so the decay meter + countdown animate between polls.
  useEffect(() => {
    const open = st?.currentQuestion?.state === 'open';
    if (!open) return;
    const id = setInterval(() => setTick((n) => n + 1), 100);
    return () => clearInterval(id);
  }, [st?.currentQuestion?.state]);

  const cqId = st?.currentQuestion?.id ?? null;
  useEffect(() => {
    setPending(null);
  }, [cqId]);

  const cq = st?.currentQuestion ?? null;
  const myAnswer = cq && pending && pending.qid === cq.id ? pending.idx : (cq?.myAnswer ?? null);
  const answered = myAnswer != null;

  const submitAnswer = async (idx: number) => {
    if (!showId || !cq || cq.state !== 'open' || answered || submitting) return;
    setPending({ qid: cq.id, idx });
    setSubmitting(true);
    try {
      const res = await gameshowApi.answer(showId, cq.id, idx);
      if (typeof res.pointsEarned === 'number') earnedRef.current[cq.id] = res.pointsEarned;
      poll();
    } catch {
      setPending(null);
    } finally {
      setSubmitting(false);
    }
  };

  // ── live clock ──
  const now = Date.now() + offsetRef.current;
  const elapsedMs = cq ? Math.max(0, now - toMs(cq.openedAt)) : 0;
  const elapsedSec = elapsedMs / 1000;
  const ptsNow = cq ? pointsNow(elapsedMs, cq.points, cq.decaySeconds) : 0;
  const meterPct = cq && cq.points ? Math.max(0, Math.min(100, (ptsNow / cq.points) * 100)) : 0;
  const remaining = cq ? Math.max(0, cq.lockSeconds - elapsedSec) : 0;

  const status = st?.status;
  const isOpen = status === 'live' && cq?.state === 'open';
  const isLocked = status === 'live' && cq?.state === 'locked';
  const isReveal = status === 'live' && cq?.state === 'revealed';

  // ── not-a-contestant gate ──
  if (notContestant) {
    return (
      <Shell>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 16,
            textAlign: 'center',
            padding: '80px 24px',
          }}
        >
          <span
            style={{
              width: 66,
              height: 66,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'rgba(255,214,0,.1)',
              border: '1px solid rgba(255,214,0,.35)',
            }}
          >
            <ShieldAlert size={28} color={GOLD} />
          </span>
          <h1 className="font-hj-display" style={{ fontSize: 34, margin: 0, color: '#fff', lineHeight: 1.05 }}>
            You haven't been called down
          </h1>
          <p style={{ fontSize: 15, color: 'rgba(255,255,255,.7)', maxWidth: 420, lineHeight: 1.5, margin: 0 }}>
            This stage page is only for contestants the host has drawn. Head back to your phone and keep playing
            along — opt in to the stage pool for a shot at being called on camera.
          </p>
        </div>
      </Shell>
    );
  }

  if (!st && !error) {
    return (
      <Shell>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: 'rgba(255,255,255,.6)', padding: 80, justifyContent: 'center' }}>
          <Loader2 size={18} className="animate-spin" /> Taking the stage…
        </div>
      </Shell>
    );
  }
  if (error && !st) {
    return (
      <Shell>
        <div style={{ color: '#ff9db3', fontSize: 15, padding: 80, textAlign: 'center' }}>{error}</div>
      </Shell>
    );
  }

  const showName = st?.title || 'Game Show';

  return (
    <Shell>
      {/* HEADER — on camera */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 16,
          flexWrap: 'wrap',
          padding: '13px 22px',
          borderBottom: '1px solid rgba(255,255,255,.08)',
          background: 'rgba(140,198,63,.05)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, ...kicker, color: GREEN }}>
          <span style={{ width: 9, height: 9, borderRadius: '50%', background: GREEN }} className="animate-pulse" />
          You're on camera
        </div>
        <div className="font-hj-display" style={{ fontSize: 18, color: GOLD }}>
          {showName}
        </div>
        <div style={{ fontSize: 12.5, color: 'rgba(255,255,255,.55)' }}>
          On-screen questions · the camera and clock are the anti-cheat
        </div>
      </div>

      {/* 2-COLUMN BODY */}
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 300px', gap: 18, padding: '20px 22px' }}>
        {/* LEFT: host stage + question */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, minWidth: 0 }}>
          {/* host cam placeholder band */}
          <div
            style={{
              position: 'relative',
              borderRadius: 16,
              overflow: 'hidden',
              aspectRatio: '16 / 9',
              background: 'repeating-linear-gradient(135deg, #1d1528 0 13px, #191322 13px 26px)',
              border: '1px solid rgba(255,255,255,.09)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {status === 'live' && (
              <div
                style={{
                  position: 'absolute',
                  top: 12,
                  left: 12,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  background: 'rgba(255,77,106,.92)',
                  color: '#fff',
                  fontSize: 9,
                  fontWeight: 700,
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  padding: '4px 9px',
                  borderRadius: 6,
                }}
              >
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#fff' }} /> Live
              </div>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, opacity: 0.55 }}>
              <Video size={15} color="rgba(255,255,255,.6)" />
              <span
                style={{
                  fontFamily: 'var(--hj-font-mono)',
                  fontSize: 10,
                  letterSpacing: '0.08em',
                  color: 'rgba(255,255,255,.5)',
                  textTransform: 'uppercase',
                }}
              >
                host cam · asking the question
              </span>
            </div>
          </div>

          {/* QUESTION PANEL — copy-blocked */}
          <div
            onCopy={blockCopy}
            onContextMenu={blockCopy}
            style={{
              userSelect: 'none',
              WebkitUserSelect: 'none',
              border: '1px solid rgba(255,255,255,.1)',
              borderRadius: 18,
              padding: '22px 24px',
              background: 'rgba(255,255,255,.03)',
              minHeight: 260,
            }}
          >
            {isOpen && cq ? (
              <OpenQuestion
                cq={cq}
                ptsNow={ptsNow}
                meterPct={meterPct}
                remaining={remaining}
                myAnswer={myAnswer}
                answered={answered}
                submitting={submitting}
                onPick={submitAnswer}
              />
            ) : isLocked && cq ? (
              <LockedReveal cq={cq} myAnswer={myAnswer} locked />
            ) : isReveal && cq ? (
              <LockedReveal cq={cq} myAnswer={myAnswer} earned={earnedRef.current[cq.id]} />
            ) : status === 'ended' ? (
              <Centered title="Show complete" sub="Great run on stage — the host is wrapping up." />
            ) : (
              <Centered
                title="Stand by"
                sub="You're on camera. The host will read you in — your question appears here the moment it opens."
              />
            )}
          </div>
        </div>

        {/* RIGHT: self-view + anti-cheat note */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, minWidth: 0 }}>
          <div
            style={{
              position: 'relative',
              borderRadius: 15,
              overflow: 'hidden',
              aspectRatio: '3 / 4',
              background: 'repeating-linear-gradient(135deg, #17202a 0 12px, #141b24 12px 24px)',
              border: `2px solid rgba(140,198,63,.45)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <div
              style={{
                position: 'absolute',
                top: 10,
                left: 10,
                fontSize: 9,
                fontWeight: 700,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                color: GREEN,
                background: 'rgba(0,0,0,.45)',
                padding: '4px 8px',
                borderRadius: 6,
              }}
            >
              Your camera
            </div>
            <span
              style={{
                fontFamily: 'var(--hj-font-mono)',
                fontSize: 10,
                letterSpacing: '0.08em',
                color: 'rgba(255,255,255,.4)',
                textTransform: 'uppercase',
                textAlign: 'center',
                padding: '0 12px',
              }}
            >
              you · on stage
            </span>
          </div>

          {/* leaderboard mini */}
          {st && st.leaderboard.me && (
            <div
              style={{
                borderRadius: 14,
                padding: 15,
                background: 'linear-gradient(90deg, rgba(255,214,0,.14), rgba(242,134,201,.1))',
                border: '1px solid rgba(255,214,0,.4)',
                display: 'flex',
                alignItems: 'center',
                gap: 12,
              }}
            >
              <span className="font-hj-display tabular" style={{ fontSize: 22, color: GOLD }}>
                #{st.leaderboard.me.rank}
              </span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--hj-ink)' }}>Your standing</div>
                <div style={{ fontSize: 11.5, color: 'rgba(255,255,255,.6)' }}>
                  {st.leaderboard.me.totalPoints.toLocaleString()} pts
                </div>
              </div>
            </div>
          )}

          <div
            style={{
              marginTop: 'auto',
              border: '1px solid rgba(255,255,255,.1)',
              borderRadius: 14,
              padding: 15,
              background: 'rgba(255,255,255,.03)',
            }}
          >
            <div style={{ ...kicker, letterSpacing: '0.14em', color: GOLD, marginBottom: 8 }}>
              Why on-screen is safe here
            </div>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,.66)', lineHeight: 1.5 }}>
              You're on camera in front of the whole room, so a second screen is impossible — the camera and the
              clock are the anti-cheat. Play it straight and fast.
            </div>
          </div>
        </div>
      </div>
    </Shell>
  );
};

// ── Shell ─────────────────────────────────────────────────────────────
const Shell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{ minHeight: '100vh', background: PAGE_BG, color: 'var(--hj-ink)', fontFamily: 'var(--hj-font-ui)', padding: '24px' }}>
    <div
      style={{
        maxWidth: 1240,
        margin: '0 auto',
        borderRadius: 16,
        overflow: 'hidden',
        border: '1px solid rgba(255,255,255,.08)',
        background: 'rgba(20,16,24,.5)',
      }}
    >
      {children}
    </div>
  </div>
);

const Centered: React.FC<{ title: string; sub: string }> = ({ title, sub }) => (
  <div
    style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 12,
      textAlign: 'center',
      minHeight: 210,
    }}
  >
    <div className="font-hj-display" style={{ fontSize: 32, color: GOLD }}>
      {title}
    </div>
    <div style={{ fontSize: 14, color: 'var(--hj-ink-2)', maxWidth: 400, lineHeight: 1.5 }}>{sub}</div>
  </div>
);

// ── OPEN QUESTION (contestant) ────────────────────────────────────────
const OpenQuestion: React.FC<{
  cq: StateCurrentQuestion;
  ptsNow: number;
  meterPct: number;
  remaining: number;
  myAnswer: number | null;
  answered: boolean;
  submitting: boolean;
  onPick: (idx: number) => void;
}> = ({ cq, ptsNow, meterPct, remaining, myAnswer, answered, submitting, onPick }) => {
  const ptsColor = meterPct > 66 ? GOLD : meterPct > 33 ? '#ff9d3f' : REC;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span
            style={{
              ...kicker,
              color: GREEN,
              border: '1px solid rgba(140,198,63,.4)',
              padding: '4px 9px',
              borderRadius: 999,
            }}
          >
            On screen
          </span>
          <span
            style={{
              ...kicker,
              color: 'rgba(255,255,255,.5)',
              border: '1px solid rgba(255,255,255,.14)',
              padding: '4px 9px',
              borderRadius: 999,
            }}
          >
            Copy blocked
          </span>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div className="font-hj-display tabular" style={{ fontSize: 34, lineHeight: 1, color: ptsColor }}>
            {ptsNow}
          </div>
          <div style={{ fontSize: 10, color: 'rgba(255,255,255,.5)' }}>{remaining.toFixed(1)}s left</div>
        </div>
      </div>

      <div style={{ fontSize: 26, fontWeight: 700, lineHeight: 1.25, color: 'var(--hj-ink)', userSelect: 'none' }}>
        {cq.text || '…'}
      </div>

      <div style={{ width: '100%', height: 10, borderRadius: 999, background: 'rgba(255,255,255,.08)', overflow: 'hidden' }}>
        <div
          style={{
            height: '100%',
            width: `${meterPct}%`,
            borderRadius: 999,
            background: 'linear-gradient(90deg,#f286c9,#ffd600)',
            transition: 'width .12s linear',
          }}
        />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 11 }}>
        {cq.options.map((opt, i) => {
          const picked = myAnswer === i;
          const dim = answered && !picked;
          return (
            <button
              key={i}
              onClick={() => onPick(i)}
              disabled={answered || submitting}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 13,
                textAlign: 'left',
                cursor: answered ? 'default' : 'pointer',
                border: `2px solid ${picked ? 'rgba(255,214,0,.9)' : 'rgba(255,255,255,.14)'}`,
                background: picked ? 'rgba(255,214,0,.12)' : 'rgba(255,255,255,.05)',
                opacity: dim ? 0.4 : 1,
                borderRadius: 14,
                padding: '15px 16px',
                transition: 'all .16s',
              }}
            >
              <span
                style={{
                  flexShrink: 0,
                  width: 30,
                  height: 30,
                  borderRadius: 9,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 700,
                  fontSize: 14,
                  background: picked ? GOLD : 'rgba(255,255,255,.1)',
                  color: picked ? '#150e20' : 'rgba(255,255,255,.85)',
                }}
              >
                {LETTERS[i]}
              </span>
              <span style={{ flex: 1, fontSize: 16, fontWeight: 600, color: 'var(--hj-ink)', userSelect: 'none' }}>{opt}</span>
              {picked && <span style={{ ...kicker, color: GOLD }}>locked</span>}
            </button>
          );
        })}
      </div>

      <div style={{ fontSize: 12.5, color: 'rgba(255,255,255,.55)', textAlign: 'center' }}>
        {answered ? 'Locked in. Hold for the host to reveal…' : 'Answer fast — points decay every second.'}
      </div>
    </div>
  );
};

// ── LOCKED / REVEAL (contestant) ──────────────────────────────────────
const LockedReveal: React.FC<{
  cq: StateCurrentQuestion;
  myAnswer: number | null;
  locked?: boolean;
  earned?: number;
}> = ({ cq, myAnswer, locked, earned }) => {
  const correct = cq.correctOption;
  const isVote = correct == null;
  const gotIt = correct != null && myAnswer === correct;
  const tally = cq.tally || [];
  const total = tally.reduce((a, b) => a + b, 0);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        {locked ? (
          <>
            <Lock size={18} color={GOLD} />
            <span className="font-hj-display" style={{ fontSize: 26, color: GOLD }}>
              Answers locked
            </span>
          </>
        ) : (
          <span
            className="font-hj-display"
            style={{ fontSize: 26, color: isVote ? GOLD : gotIt ? GREEN : PINK }}
          >
            {isVote ? 'Votes in' : gotIt ? (earned != null ? `+${earned}` : 'You got it') : myAnswer == null ? 'No answer' : 'Missed it'}
          </span>
        )}
      </div>

      <div style={{ fontSize: 22, fontWeight: 700, lineHeight: 1.25, color: 'var(--hj-ink)', userSelect: 'none' }}>
        {cq.text || '…'}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 11 }}>
        {cq.options.map((opt, i) => {
          const isCorrect = correct === i;
          const isMine = myAnswer === i;
          const pct = total ? Math.round(((tally[i] || 0) / total) * 100) : 0;
          let border = 'rgba(255,255,255,.1)';
          let bg = 'rgba(255,255,255,.03)';
          let fg: string = 'rgba(255,255,255,.6)';
          let letterBg = 'rgba(255,255,255,.08)';
          let letterFg = 'rgba(255,255,255,.6)';
          let tag = '';
          let tagColor = GOLD;
          if (!locked && isCorrect) {
            border = GREEN;
            bg = 'rgba(140,198,63,.1)';
            fg = 'var(--hj-ink)';
            letterBg = GREEN;
            letterFg = '#08110c';
            tag = isMine ? '✓ you' : '✓';
            tagColor = GREEN;
          } else if (isMine) {
            border = locked ? 'rgba(255,214,0,.7)' : PINK;
            bg = locked ? 'rgba(255,214,0,.1)' : 'rgba(242,134,201,.08)';
            fg = 'var(--hj-ink)';
            tag = locked ? 'your pick' : 'your pick';
            tagColor = locked ? GOLD : PINK;
          }
          return (
            <div
              key={i}
              style={{
                position: 'relative',
                overflow: 'hidden',
                border: `2px solid ${border}`,
                background: bg,
                borderRadius: 14,
                padding: '14px 15px',
                display: 'flex',
                alignItems: 'center',
                gap: 12,
              }}
            >
              <span
                style={{
                  flexShrink: 0,
                  width: 28,
                  height: 28,
                  borderRadius: 8,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 700,
                  fontSize: 13,
                  background: letterBg,
                  color: letterFg,
                }}
              >
                {LETTERS[i]}
              </span>
              <span style={{ flex: 1, fontSize: 15, fontWeight: 600, color: fg, userSelect: 'none' }}>{opt}</span>
              {tag && <span style={{ ...kicker, fontSize: 9, color: tagColor }}>{tag}</span>}
              {!locked && (
                <span className="font-hj-display tabular" style={{ fontSize: 16, color: fg }}>
                  {pct}%
                </span>
              )}
            </div>
          );
        })}
      </div>

      <div style={{ fontSize: 12.5, color: 'rgba(255,255,255,.55)', textAlign: 'center' }}>
        {locked ? 'Host is about to reveal — stay in frame.' : 'Host is reacting on stream…'}
      </div>
    </div>
  );
};

export default GameShowContestantPage;
